import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X, CalendarCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { BOOKING_URL } from "@/lib/api";

const LINKS = [
  { label: "Services", target: "#solution" },
  { label: "How It Works", target: "#how-it-works" },
  { label: "About", target: "#founder" },
  { label: "Local Services", target: "#local-services" },
  { label: "Case Studies", target: "#case-study" },
  { label: "Contact", target: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (target) => {
    setOpen(false);
    const el = document.querySelector(target);
    if (el && lenis) lenis.scrollTo(el, { offset: -80 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-navy/85 backdrop-blur-xl border-b border-gold/20"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
          <button
            onClick={() => scrollTo("#hero")}
            data-testid="nav-logo-button"
            className="shrink-0"
            aria-label="BAT Ear Research home"
          >
            <Logo dark markSize={34} />
          </button>

          <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
            {LINKS.map((l) => (
              <button
                key={l.target}
                onClick={() => scrollTo(l.target)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="nav-link text-white/80 hover:text-white text-sm font-medium tracking-wide"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-cta"
              className="hidden sm:inline-flex items-center gap-2 bg-gold text-navy-deep hover:bg-gold-light transition-colors duration-300 font-semibold px-5 py-3 text-[13px] uppercase tracking-wider"
            >
              <CalendarCheck size={16} />
              Book Free Consultation
            </a>
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="fixed top-[76px] left-0 right-0 z-40 bg-navy-deep/97 backdrop-blur-xl border-b border-gold/20 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.target}
                  onClick={() => scrollTo(l.target)}
                  className="text-left text-white/85 hover:text-gold py-3 text-lg font-display border-b border-white/5"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center justify-center gap-2 bg-gold text-navy-deep font-semibold px-5 py-4 text-sm uppercase tracking-wider"
              >
                <CalendarCheck size={16} />
                Book Free Consultation
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
